import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronsUpDown, CreditCard, LogOut, UserRound, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "@/services/auth";
import { DEMO_MODE } from "@/demo/config";
import { useDemo } from "@/demo/state";
import { useCoachStatus } from "@/app/coach/use-coach-status";

/**
 * Profile row + popover with the account actions (manage/cancel subscription,
 * sign out). Lives in the sidebar's bottom corner on desktop and as the
 * "Account" item in the mobile nav — the usual place, off the coach page.
 */
export function AccountMenu({ variant = "sidebar" }: { variant?: "sidebar" | "mobile" }) {
  const { user, signOut } = useAuth();
  const demo = useDemo();
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  // The coach's TikTok profile (shared, cached coach status) stands in for the
  // account when it is there: picture, display name, then @handle.
  const { seat } = useCoachStatus();
  const handle = seat?.tiktok_username?.trim() || "";
  const avatarUrl = seat?.tiktok_avatar_url || null;
  const [avatarBroken, setAvatarBroken] = useState(false);
  const showAvatar = !!avatarUrl && !avatarBroken;
  const name = seat?.tiktok_display_name?.trim() || (handle ? `@${handle}` : DEMO_MODE ? demo.name : user?.full_name || "BMC creator");
  const email = DEMO_MODE ? "jordan@example.com" : user?.email || "";
  const initial = (name.replace(/^@/, "").trim()[0] || "B").toUpperCase();
  const avatar = (size: number) => (
    <span className="account-avatar" aria-hidden="true" style={{ width: size, height: size }}>
      {showAvatar
        ? <img src={avatarUrl!} alt="" width={size} height={size} referrerPolicy="no-referrer" onError={() => setAvatarBroken(true)} />
        : initial}
    </span>
  );

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className={"account-menu " + variant} ref={ref}>
      <button type="button" className="account-trigger" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        {variant === "sidebar" ? (
          <>
            {avatar(30)}
            <span className="account-text"><span className="account-name">{name}</span><br /><span className="account-email">{email}</span></span>
            <ChevronsUpDown size={15} aria-hidden="true" />
          </>
        ) : (
          <>{showAvatar || handle ? avatar(21) : <UserRound size={21} />}<span>Account</span></>
        )}
      </button>
      {open && (
        <div className="account-popover" role="menu" aria-label="Account">
          <div className="account-popover-head">
            {avatar(36)}
            <span className="account-text"><span className="account-name">{name}</span><br /><span className="account-email">{email}</span></span>
          </div>
          <Link role="menuitem" to="/settings" onClick={() => setOpen(false)}><SettingsIcon size={15} /> Settings</Link>
          <Link role="menuitem" to="/billing" onClick={() => setOpen(false)}><CreditCard size={15} /> Billing</Link>
          {!DEMO_MODE && <button type="button" role="menuitem" onClick={() => { setOpen(false); signOut().catch(() => setNotice("Could not sign out. Please try again.")); }}><LogOut size={15} /> Sign out</button>}
        </div>
      )}
      {notice && <p className="muted" role="status" style={{ fontSize: 12, padding: "6px 12px 0" }}>{notice}</p>}
    </div>
  );
}
